import { usePatchFileRecordRegion } from "@/api/fileRecords";
import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import Sentry from "@/integrations/Sentry";
import { useFileRecordExport } from "@/panes/file-record-detail/FileRecordExportContext";
import { FileRecordSelectionActionOverlay } from "@/panes/file-record-detail/FileRecordSelectionActionOverlay";
import {
  useHtmlInlineEditController,
  useHtmlInlineEditState,
} from "@/panes/file-record-detail/HtmlInlineEditContext";
import {
  attachIframeTextSelectionListeners,
  type IframeTextSelectionState,
} from "@/panes/file-record-detail/useDomTextSelectionAction";
import type { HtmlBlockPaneContext } from "@/panes/file-record-detail/useOpenFileRecord";
import { usePushFileRecordTextSelectionToChat } from "@/panes/file-record-detail/usePushFileRecordTextSelectionToChat";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  computeHtmlSrcDocFingerprint,
  getCohesiveBlockTokensCss,
  HtmlDocument,
  readIslandInnerHtml,
  useHtmlIslandAutosave,
  usesCohesiveBlockTokens,
  useThemeContext,
  type HtmlDocumentHandle,
  type SelectionAnchorRect,
} from "@cohesive/ui-components";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";
import {
  HtmlInfoView,
  HtmlSourceView,
  HtmlViewBar,
  type HtmlPaneView,
} from "./HtmlBackstage";
import { useHtmlReferencedIds } from "./htmlReferencedIds";

/**
 * After a local save, suppress SSE-triggered re-fetches for this long.
 * The DOM already shows the correct content; re-delivering would spend a
 * presign round trip and a morph on visually identical content.
 */
const IGNORE_SSE_AFTER_SAVE_MS = 3_000;

/**
 * How long a fingerprint keeps blocking re-delivery after being SUPERSEDED
 * by a newer one. The clock must run from supersession, not from original
 * delivery: the hash flip-flop this guards against (a stale writer regresses
 * the record cache to the pre-write hash after the corrected one already
 * landed via the S3-event pipeline) reaches back to a fingerprint that may
 * have been delivered arbitrarily long ago, but always one that was replaced
 * only moments earlier. The window must also stay short: a genuine return to
 * an earlier hash — agent undo, version restore — has to deliver once the
 * flip-flop window has passed.
 */
const SUPERSEDED_FINGERPRINT_TTL_MS = 15_000;

/**
 * A rewritten HTML body plus the fingerprint inputs captured when it was
 * produced. Delivered to `HtmlDocument` as one atom: `content_hash` updates on
 * the live record before the re-fetched body lands (the body query is keyed on
 * it), and `HtmlDocument`'s fingerprint prefers the hash over the body — so
 * passing the live hash alongside a lagging html would commit the new
 * fingerprint against the old document and gate out the real update forever.
 */
interface DeliveredDocument {
  html: string;
  /** `record.content_hash` at the time `html` was produced. */
  contentHash: string | null;
  /** Known-missing presign ids at delivery time (fingerprint salt). */
  extras: string[];
}

/**
 * HTML file record viewer with island-based inline editing.
 *
 * Fetches/presigns the body, then renders it through shared `HtmlDocument`.
 * Islands are `[data-edit-id]` elements annotated by the agent. Changes
 * autosave after 5s of inactivity; pending count and save status appear in
 * the pane header beside Publish.
 *
 * SSE-triggered re-fetches are suppressed briefly after a local save to avoid
 * an unnecessary reload. Content identity is gated by
 * `computeHtmlSrcDocFingerprint` (content or presign-fallback state), not
 * rotating presigned URL tokens.
 */

/** Breathing room around the true-size preview stage. */
const PREVIEW_STAGE_PADDING_PX = 24;

export function HtmlContent({
  record,
  blockContext,
}: {
  record: FileRecord;
  /** Canvas-block context (authored size + display flags) when the pane was
   *  opened from a block node; absent from search/list entry points. */
  blockContext?: HtmlBlockPaneContext;
}) {
  const { mutateAsync: patchRegions } = usePatchFileRecordRegion(record.id);
  const { setHtmlSource } = useFileRecordExport();
  const { previewMediaUrl, isPreviewing } = useFileRecordVersionPreview();
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const { data: htmlMeta, isError: bodyFetchFailed } = useHtmlReferencedIds(
    record,
    { enabled: !isPreviewing },
  );

  const [isFirstLoading, setIsFirstLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<DeliveredDocument | null>(null);
  const [iframeSelectionText, setIframeSelectionText] = useState("");
  const [iframeSelectionAnchor, setIframeSelectionAnchor] =
    useState<SelectionAnchorRect | null>(null);

  const { enabled: textSelectionEnabled, pushSelection } =
    usePushFileRecordTextSelectionToChat({
      fileRecordId: record.id,
      fileRecordName: getFileRecordName(record),
    });

  const handleIframeSelectionChange = useCallback(
    ({ selectedText, anchorRect }: IframeTextSelectionState) => {
      setIframeSelectionText(selectedText);
      setIframeSelectionAnchor(anchorRect);
    },
    [],
  );

  const clearIframeSelection = useCallback(() => {
    setIframeSelectionText("");
    setIframeSelectionAnchor(null);
  }, []);

  const htmlDocRef = useRef<HtmlDocumentHandle>(null);
  const selectionCleanupRef = useRef<(() => void) | null>(null);
  const hasEverShownContentRef = useRef(false);
  /** Fingerprint of the currently delivered content. Re-delivering it is
   *  always skipped — no TTL, it is identical to what is already shown. */
  const lastDeliveredFingerprintRef = useRef<string | null>(null);
  /**
   * Fingerprints replaced by a newer delivery, stamped with WHEN they were
   * superseded. Needed because the backend's post-save churn can regress
   * `content_hash` in the record cache (a region PATCH publishes the hash
   * read BEFORE the write; the corrected hash lands seconds later via the
   * async S3-event pipeline): without this, the superseded body re-delivers
   * on the way down and the current one again on the way up — two visible
   * updates for zero content change.
   *
   * Entries block re-delivery for {@link SUPERSEDED_FINGERPRINT_TTL_MS}
   * from their supersession, so the flip-flop is absorbed regardless of how
   * long ago the older content was originally shown, while a genuine revert
   * to an earlier hash (agent undo, version restore) delivers once the
   * window has passed.
   */
  const supersededFingerprintsRef = useRef<
    { fingerprint: string; supersededAt: number }[]
  >([]);
  const justSavedTimestampRef = useRef<number>(0);
  const [presignRetryNonce, setPresignRetryNonce] = useState(0);

  // One presign implementation for every HTML surface (this pane and the
  // canvas block card). It owns the presigned↔public maps, including the
  // accumulating reverse map that keeps island saves from persisting URLs
  // that expire.
  const presign = useHtmlPresignRewrite({ fileRecordId: record.id });

  // The hoisted autosave engine is presentation-agnostic; this pane routes its
  // state into the header indicator, the canvas routes it into the grip pill.
  const { setAutosaveState } = useHtmlInlineEditController();

  // Read islands from the LIVE same-origin document at save time, so a save
  // always reflects the current caret state rather than a stale snapshot.
  // (The canvas block editor supplies the same callback backed by a map fed
  // by runtime messages, because its iframe's DOM is unreachable.)
  const readIslandContent = useCallback((editId: string) => {
    const doc = htmlDocRef.current?.getDocument();
    if (!doc) return null;
    const el = doc.querySelector<HTMLElement>(`[data-edit-id="${editId}"]`);
    // Not `el.innerHTML`: the editable prepare pass marker-injects scaffolding
    // (button tabindex/type) into islands, exactly like the canvas runtime
    // does in its frames. Strip it the same way the canvas does — via the
    // shared marker bookkeeping — or a region PATCH would persist it into the
    // stored document, and baselines would disagree with these reads.
    return el ? readIslandInnerHtml(el) : null;
  }, []);

  const onSaveSuccess = useCallback(() => {
    justSavedTimestampRef.current = Date.now();
  }, []);

  const {
    getDirtyEditIds,
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    flushDirtyRegions,
    resetAutosave,
  } = useHtmlIslandAutosave({
    readIslandContent,
    patchRegions,
    onSaveSuccess,
    // Islands read off a rewritten DOM carry presigned URLs; reverse them so a
    // save never persists a URL that expires. Applied uniformly inside the
    // hook (baselines, dirty compares, payloads).
    sanitizeContent: presign.reversePresignedUrls,
    onAutosaveState: setAutosaveState,
  });

  const { pendingCount, isSaving } = useHtmlInlineEditState();

  const protectedRegionIds = useMemo(
    () => getDirtyEditIds(),
    [getDirtyEditIds, pendingCount, isSaving],
  );

  // Viewers (no edit permission) get interactive mode; the region patch
  // endpoint also enforces this server-side. Default unknown capability to
  // editable (fail-open) — list/mutation cache writes drop can_edit on this
  // record's per-id entry, and failing closed would make the editor
  // intermittently go read-only. Server stays authoritative.
  const mode = (record.can_edit ?? true) ? "edit" : "interactive";

  useEffect(() => {
    presign.reset();
    lastDeliveredFingerprintRef.current = null;
    supersededFingerprintsRef.current = [];
    hasEverShownContentRef.current = false;
    setDelivered(null);
    clearIframeSelection();
    resetAutosave();

    return () => {
      try {
        selectionCleanupRef.current?.();
      } catch {
        /* ignore */
      }
      selectionCleanupRef.current = null;
      resetAutosave({ flush: true });
    };
  }, [record.id, resetAutosave, clearIframeSelection]);

  useEffect(() => {
    if (isPreviewing) {
      setPreviewLoaded(false);
    }
  }, [isPreviewing, previewMediaUrl]);

  useEffect(() => {
    if (isPreviewing) {
      return;
    }
    lastDeliveredFingerprintRef.current = null;
    supersededFingerprintsRef.current = [];
    hasEverShownContentRef.current = false;
    setDelivered(null);
  }, [isPreviewing]);

  useEffect(() => {
    if (isPreviewing) {
      setHtmlSource(null);
      return () => setHtmlSource(null);
    }
    if (!record.url || bodyFetchFailed || !htmlMeta) {
      setHtmlSource(null);
      return;
    }
    setHtmlSource(htmlMeta.body);
    return () => {
      setHtmlSource(null);
    };
  }, [isPreviewing, record.url, htmlMeta, bodyFetchFailed, setHtmlSource]);

  useEffect(() => {
    if (isPreviewing) return;
    if (bodyFetchFailed) {
      setFetchError("Failed to load HTML");
      setIsFirstLoading(false);
      return;
    }

    if (!htmlMeta) {
      if (!hasEverShownContentRef.current) {
        setIsFirstLoading(!!record.url);
      }
      return;
    }

    if (
      hasEverShownContentRef.current &&
      Date.now() - justSavedTimestampRef.current < IGNORE_SSE_AFTER_SAVE_MS
    ) {
      return;
    }

    if (!hasEverShownContentRef.current) {
      setIsFirstLoading(true);
    }

    let cancelled = false;

    void (async () => {
      try {
        const rewritten = await presign.transformHtml(htmlMeta.body);
        if (cancelled) return;

        setFetchError(null);

        const knownMissingIds = presign.getKnownMissingIds();
        const fingerprint = computeHtmlSrcDocFingerprint(
          htmlMeta.body,
          record.content_hash,
          knownMissingIds,
        );
        if (fingerprint === lastDeliveredFingerprintRef.current) return;

        const now = Date.now();
        const superseded = supersededFingerprintsRef.current.filter(
          (entry) => now - entry.supersededAt < SUPERSEDED_FINGERPRINT_TTL_MS,
        );
        supersededFingerprintsRef.current = superseded;
        if (superseded.some((entry) => entry.fingerprint === fingerprint)) {
          return;
        }

        const outgoing = lastDeliveredFingerprintRef.current;
        if (outgoing !== null) {
          supersededFingerprintsRef.current = [
            ...superseded
              .filter((entry) => entry.fingerprint !== outgoing)
              .slice(-3),
            { fingerprint: outgoing, supersededAt: now },
          ];
        }
        lastDeliveredFingerprintRef.current = fingerprint;
        // Deliver html + hash + extras as one atom so HtmlDocument's
        // fingerprint inputs can never desynchronize (see DeliveredDocument).
        setDelivered({
          html: rewritten,
          contentHash: record.content_hash ?? null,
          extras: [...knownMissingIds].sort(),
        });
      } catch (err) {
        if (cancelled) return;
        setFetchError(err instanceof Error ? err.message : "Failed to load");
        setIsFirstLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    htmlMeta,
    bodyFetchFailed,
    record.url,
    record.id,
    record.content_hash,
    isPreviewing,
    presignRetryNonce,
  ]);

  const attachSelectionListeners = useCallback(() => {
    try {
      selectionCleanupRef.current?.();
    } catch {
      /* ignore */
    }
    selectionCleanupRef.current = null;

    if (!textSelectionEnabled) return;

    const doc = htmlDocRef.current?.getDocument();
    const iframeEl = doc?.defaultView?.frameElement;
    if (!(iframeEl instanceof HTMLIFrameElement)) return;

    selectionCleanupRef.current = attachIframeTextSelectionListeners({
      iframeEl,
      enabled: true,
      onSelectionChange: handleIframeSelectionChange,
    });
  }, [textSelectionEnabled, handleIframeSelectionChange]);

  const handleDocumentLoad = useCallback(() => {
    if (!hasEverShownContentRef.current) {
      hasEverShownContentRef.current = true;
      setIsFirstLoading(false);
    }
    resetAutosave();
    attachSelectionListeners();
  }, [resetAutosave, attachSelectionListeners]);

  // A full iframe reload (edit/interactive mode switch, color-scheme flip or
  // morph fallback) discards the live DOM. Persist pending island edits first,
  // since the subsequent load resets autosave baselines. The flush keeps
  // `isSaving` set until its PATCH settles, so Publish stays gated until the
  // edits land.
  const handleBeforeReload = useCallback(() => {
    resetAutosave({ flush: true });
  }, [resetAutosave]);

  useEffect(() => {
    if (!delivered) return;
    attachSelectionListeners();
  }, [delivered, attachSelectionListeners]);

  useEffect(() => {
    return () => {
      try {
        selectionCleanupRef.current?.();
      } catch {
        /* ignore */
      }
      selectionCleanupRef.current = null;
    };
  }, []);

  // Canvas blocks know they are token-styled from the node's `presentation`
  // marker. The pane is addressed by file-record id and has no node data, so
  // it sniffs the content instead: documents written against the Cohesive
  // token vocabulary render theme-matched, everything else keeps the legacy
  // force-light "paper" look.
  const { mode: themeMode } = useThemeContext();
  const tokenStyled = useMemo(
    () => (delivered ? usesCohesiveBlockTokens(delivered.html) : false),
    [delivered],
  );
  const documentColorScheme = tokenStyled
    ? themeMode === "dark"
      ? "dark"
      : "light"
    : undefined;
  // Tokens must travel with the scheme — the document references vars that an
  // opaque preview frame cannot inherit from the app. Delivered via
  // `extraHeadCss` + `injectFontLinks`, the SAME transport the canvas block
  // card uses: prepended to <head>, so agent CSS wins cascade ties over the
  // injected tokens identically on both surfaces. (Delivering these through
  // `customCss` would append them instead — tokens would override agent CSS
  // in the pane but not on the canvas.)
  const tokenCss = useMemo(
    () => (tokenStyled ? getCohesiveBlockTokensCss() : undefined),
    [tokenStyled],
  );

  // ── Backstage views ───────────────────────────────────────────────────────
  // The pane is the block's backstage, not a competing renderer: Preview
  // shows the document at its authored canvas size (scaled to fit), Source
  // and Info carry what the canvas card can't show.
  const [view, setView] = useState<HtmlPaneView>("preview");
  const handleViewChange = useCallback(
    (next: HtmlPaneView) => {
      if (next === view) return;
      // Leaving Preview unmounts the live document; save pending island
      // edits while their DOM is still readable.
      if (view === "preview") flushDirtyRegions();
      setView(next);
    },
    [view, flushDirtyRegions],
  );

  const [previewScaleMode, setPreviewScaleMode] = useState<"fit" | "actual">(
    "fit",
  );
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [previewAvailWidth, setPreviewAvailWidth] = useState<number | null>(
    null,
  );
  // Layout effect so the first paint already has the fitted scale (an effect
  // would flash the block at 100% for one frame).
  useLayoutEffect(() => {
    if (!blockContext || view !== "preview") return;
    const el = previewContainerRef.current;
    if (!el) return;
    const measure = () => setPreviewAvailWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [blockContext, view]);

  // Fit-to-width (vertical scroll stays natural); 100% scrolls both ways.
  const fitScale =
    blockContext && previewAvailWidth != null
      ? Math.min(
          1,
          Math.max(
            0.1,
            (previewAvailWidth - PREVIEW_STAGE_PADDING_PX * 2) /
              blockContext.width,
          ),
        )
      : 1;
  const previewScale = previewScaleMode === "actual" ? 1 : fitScale;

  const handleAssetError = useCallback(
    (url: string) => {
      // Newly-missing asset: stop presigning that id and re-deliver, so the
      // document falls back to the stable public URL instead of re-requesting
      // a dead presigned one on every future update.
      if (presign.markMissingByPresignedUrl(url)) {
        setPresignRetryNonce((n) => n + 1);
      }
    },
    [presign],
  );

  if (isPreviewing) {
    if (!previewMediaUrl) {
      return (
        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
          Version preview URL not available for this file.
        </div>
      );
    }
    return (
      <div className="relative h-full w-full">
        <iframe
          key={previewMediaUrl}
          src={previewMediaUrl}
          title={getFileRecordName(record)}
          className={cn(
            "h-full w-full border-0 transition-opacity duration-300",
            previewLoaded ? "opacity-100" : "opacity-0",
          )}
          sandbox="allow-same-origin allow-scripts allow-popups"
          onLoad={() => setTimeout(() => setPreviewLoaded(true), 300)}
        />
        {!previewLoaded && <LoadingOverlay />}
      </div>
    );
  }

  if (!record.url && !isFirstLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        HTML not available.
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        Failed to load HTML: {fetchError}
      </div>
    );
  }

  const documentEl = delivered && (
    <HtmlDocument
      ref={htmlDocRef}
      html={delivered.html}
      mode={mode}
      title={getFileRecordName(record)}
      contentHash={delivered.contentHash}
      fingerprintExtras={delivered.extras}
      protectedRegionIds={protectedRegionIds}
      colorScheme={documentColorScheme}
      extraHeadCss={tokenCss}
      injectFontLinks={tokenStyled}
      onRegionFocus={handleIslandFocus}
      onRegionEdit={handleIslandInput}
      onRegionBlur={handleIslandFocusOut}
      onSaveRequest={flushDirtyRegions}
      onAssetError={handleAssetError}
      onLoad={handleDocumentLoad}
      onBeforeReload={handleBeforeReload}
      onMorphError={(err) => {
        Sentry.captureException(err, {
          tags: { feature: "html-iframe-morph" },
          extra: { fileRecordId: record.id },
        });
      }}
    />
  );

  const previewOverlays = (
    <>
      {isFirstLoading && <LoadingOverlay />}
      {textSelectionEnabled && (
        <FileRecordSelectionActionOverlay
          selectedText={iframeSelectionText}
          anchorRect={iframeSelectionAnchor}
          onActivate={pushSelection}
          onClear={clearIframeSelection}
        />
      )}
    </>
  );

  const frameless = blockContext?.presentation.frameless === true;
  const transparent = blockContext?.presentation.transparent === true;

  // The backstage (view bar + true-size preview) is a BLOCK surface —
  // standard HTML documents (and anything opened without node context, e.g.
  // from search) keep the plain full-bleed reflow pane.
  if (!blockContext) {
    return (
      <div
        className="relative h-full w-full"
        style={{ colorScheme: documentColorScheme ?? "light" }}
      >
        {documentEl}
        {previewOverlays}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <HtmlViewBar
        view={view}
        onViewChange={handleViewChange}
        right={
          view === "preview" ? (
            <>
              <span className="tabular-nums text-[11px] text-muted-foreground">
                {blockContext.width} × {blockContext.height}
                {previewScaleMode === "fit" && previewScale < 1
                  ? ` · ${Math.round(previewScale * 100)}%`
                  : ""}
              </span>
              <div className="flex overflow-hidden rounded-md border">
                {(["fit", "actual"] as const).map((scaleMode) => (
                  <button
                    key={scaleMode}
                    type="button"
                    aria-pressed={previewScaleMode === scaleMode}
                    onClick={() => setPreviewScaleMode(scaleMode)}
                    className={cn(
                      "px-1.5 py-0.5 text-[11px] transition-colors",
                      previewScaleMode === scaleMode
                        ? "bg-muted font-medium text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {scaleMode === "fit" ? "Fit" : "100%"}
                  </button>
                ))}
              </div>
            </>
          ) : undefined
        }
      />
      <div className="relative min-h-0 flex-1">
        {view === "source" ? (
          <HtmlSourceView body={htmlMeta?.body ?? null} />
        ) : view === "info" ? (
          <HtmlInfoView record={record} blockContext={blockContext} />
        ) : (
          // True-size preview: the document at its authored canvas footprint
          // on a muted stage, scaled to fit the pane width. No shield here —
          // there is no canvas to pan, so the block is directly interactive
          // (and comfortably editable) the moment the pointer is over it.
          <div
            className="relative h-full w-full"
            style={{ colorScheme: documentColorScheme ?? "light" }}
          >
            <div
              ref={previewContainerRef}
              className="h-full w-full overflow-auto bg-muted/20"
            >
              <div
                className="flex min-h-full w-max min-w-full items-start justify-center"
                style={{ padding: PREVIEW_STAGE_PADDING_PX }}
              >
                {/* Outer div reserves the SCALED footprint (transforms don't
                    affect layout); inner div is the authored-size box. */}
                <div
                  className="relative shrink-0"
                  style={{
                    width: blockContext.width * previewScale,
                    height: blockContext.height * previewScale,
                  }}
                >
                  <div
                    className={cn(
                      "overflow-hidden",
                      !frameless && "rounded-xl border shadow-sm",
                      !frameless && !transparent && "bg-card",
                    )}
                    style={{
                      width: blockContext.width,
                      height: blockContext.height,
                      transform:
                        previewScale !== 1
                          ? `scale(${previewScale})`
                          : undefined,
                      transformOrigin: "top left",
                      colorScheme: documentColorScheme ?? "light",
                    }}
                  >
                    {documentEl}
                  </div>
                </div>
              </div>
            </div>
            {previewOverlays}
          </div>
        )}
      </div>
    </div>
  );
}
